import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Observable, filter, map, takeUntil, withLatestFrom } from 'rxjs';
import { NgxsFormPluginModule, UpdateFormValue } from '@ngxs/form-plugin';
import { Select, ofActionSuccessful } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';

import { DataUpdateResponseWithAttachmentUrl, DropDownItem, WebApiResponse, WebApiResponseBase } from '@app/shared/classes/general';
import { DatepickerNormalFormat, NgxsActionName, NgxsFormPath, SelectItems } from '@app/shared/constants';
import { AngularMaterialModule } from '@app/shared/modules/angular-material.module';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { EditModalDialogBasedComponent } from '@shared/components/edit-modal-dialog/edit-modal-dialog.based.component';
import { SpinnerButtonComponent } from '@shared/components/spinner-button/spinner-button.component';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { TeamMessage } from '@app/shared/classes/team-message';
import { InjectDialogData } from '@shared/components/edit-modal-dialog/edit-modal-dialog.constant';
import { Status, TeamMessageReceiverType } from '@app/shared/enums';
import { Driver } from '@shared/classes/driver';
import { UserProfileSearchGridComponent } from '@shared/components/user-profile-search-grid/user-profile-search-grid.component';
import { UserProfile } from '@app/shared/classes/user-profile';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { SiteConfigHelper } from '@app/shared/utils/site-config.helper';
import { SubRegion } from '@app/shared/classes/subRegion';
import { TeamMessageFieldName } from '@app/pages/team-message/team-message.constant';
import { TeamMessageHelper } from '@app/pages/team-message/team-message.helper';
import { Terminal } from '@app/shared/classes/terminal';
import { TerminateTask } from '@app/shared/actions/general.action';
import { CommonDataState } from '@app/shared/stores/common-data.state';

@Component({
  selector: 'app-team-message-edit-modal-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, SpinnerButtonComponent, TranslateModule, NgxsFormPluginModule, ReactiveFormsModule, MatDialogModule, AngularMaterialModule, UserProfileSearchGridComponent],
  templateUrl: './team-message-edit-modal-dialog.component.html',
  styleUrl: './team-message-edit-modal-dialog.component.css',
  providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: DatepickerNormalFormat },
  ],
})
export class TeamMessageEditModalDialogComponent extends EditModalDialogBasedComponent<TeamMessageEditModalDialogComponent, TeamMessage> {
  formPath = NgxsFormPath.TEAM_MESSAGE_EDIT_FORM;
  teamMessageReceiverType = TeamMessageReceiverType;
  statusSelectItems = SelectItems.statusSelectItems;

  countrySelectItems: DropDownItem[] = [];
  countrySelectVisiable: boolean = true;
  subRegionSelectVisable: boolean = true;
  title = "";

  driverList$: Observable<Driver[]>;
  subRegionSelectItems$: Observable<DropDownItem[]>;
  terminalSelectItems$: Observable<DropDownItem[]>;
  dirverSelectItems$: Observable<DropDownItem[]>;

  @Select(CommonDataState.profileMe) profileMe$: Observable<UserProfile>;
  @Select(AppDataState.taskExecuting(NgxsActionName.UPDATE_TEAM_MESSAGE)) processing$: Observable<boolean>;
  @Select(AppDataState.taskError(NgxsActionName.UPDATE_TEAM_MESSAGE)) errorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskResponse(NgxsActionName.UPDATE_TEAM_MESSAGE)) successResponse$: Observable<DataUpdateResponseWithAttachmentUrl>;

  @Select(AppDataState.taskResponse(NgxsActionName.GET_SUB_REGIONS)) subRegions$: Observable<WebApiResponse<SubRegion[]>>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_DRIVER_RELATED_TERMINAL_LIST)) terminals$: Observable<Terminal[]>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_DRIVER_BY_TERMINAL)) drivers$: Observable<WebApiResponse<Driver[]>>;

  constructor(public dialogRef: MatDialogRef<TeamMessageEditModalDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: InjectDialogData<TeamMessage>) {
    super();
    this.dataInit(dialogRef, data);
    this.isCreate = data.isCreate;
    this.initForm(data);
    this.title = data.isCreate ? "ml_144036" : "ml_143925";
  }

  initForm = (data: InjectDialogData<TeamMessage>) => {
    if (data.isCreate) {
      this.editForm = this.formBuilder.group({
        teamMessage_Id: -1,
        title: ["", Validators.required],
        body: ["", Validators.required],
        teamMessageReceiverType: ["", Validators.required],
        country: [""],
        subRegion_Id: [null],
        terminal_Ids: [[]],
        driverTerminals: [[]],
        driver_Ids: [[]],
        attachment: [""],
        status: [Status.ACTIVE, Validators.required],
      });
    }
    else {
      var type = TeamMessageHelper.GetReveiverType(data.data);
      var subRegion_Ids = data.data.subRegion_Ids;
      this.editForm = this.formBuilder.group({
        teamMessage_Id: data.data.teamMessage_Id,
        title: [data.data.title, Validators.required],
        body: [data.data.body, Validators.required],
        teamMessageReceiverType: [type],
        country: [data.data.countries ? data.data.countries[0] : ''],
        subRegion_Id: [subRegion_Ids && subRegion_Ids.length > 0 ? subRegion_Ids[0] : null],
        terminal_Ids: [data.data.terminal_Ids ?? []],
        driverTerminals: [[]],
        driver_Ids: [data.data.driver_Ids ?? []],
        attachment: [data.data.attachment],
        status: [data.data.status, Validators.required],
      });
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initByProfileMe();
    this.initSelectItems();

    this.actions$.pipe(ofActionSuccessful(AppDataAction.UpdateTeamMessage), withLatestFrom(this.successResponse$, this.errorResponse$), takeUntil(this.destroy$)).subscribe(
      ([_, successResponse, errorResponse]) => this.processAfterUpdated(successResponse, errorResponse));
  }

  initSelectItems = () => {
    this.subRegionSelectItems$ = this.subRegions$.pipe(withLatestFrom(this.profileMe$), map(([subRegions, profileMe]) => {
      var subRegion_Id = this.getSubRegionIdIfNoTerminal(profileMe);
      var filteredSubRegions = subRegion_Id === null ?
        subRegions.data.filter(item => item.country === profileMe.country) :
        subRegions.data.filter(item => item.subRegion_Id === subRegion_Id);
      return filteredSubRegions.map(item => { return { value: item.subRegion_Id, label: item.subRegionName } as DropDownItem; })
    }));

    this.terminalSelectItems$ = this.terminals$.pipe(withLatestFrom(this.profileMe$), map(([terminals, profileMe]) => {
      var terminal_Ids = this.getTerminalIds(profileMe);
      var subRegion_Id = this.getSubRegionIdIfNoTerminal(profileMe);

      var filteredTerminals = terminal_Ids.length > 0 ? terminals.filter(item => terminal_Ids.includes(item.terminal_Id)) :
        subRegion_Id !== null ? terminals.filter(item => item.subRegion?.subRegion_Id === subRegion_Id) :
          terminals.filter(item => item.country === profileMe.country);

      return filteredTerminals.map(item => { return { value: item.terminal_Id, label: `${item.terminalId} ${item.terminalName}` } as DropDownItem; })
    }));

    this.dirverSelectItems$ = this.drivers$.pipe(filter(Boolean), map(drivers =>
      drivers.data.map(item => { return { value: item.driver_Id, label: item.fullName } as DropDownItem; })));
  }

  initByProfileMe = () => {
    if (!this.isCreate) {
      return;
    }

    this.profileMe$.pipe(takeUntil(this.destroy$)).subscribe(profileMe => {
      var terminals = this.getTerminalIds(profileMe);
      var subRegion_Id = this.getSubRegionIdIfNoTerminal(profileMe);
      var country = subRegion_Id === null ? profileMe.country : "";
      var teamMessageReceiverType = "";
      if (terminals && terminals.length > 0) {
        this.countrySelectVisiable = false;
        this.subRegionSelectVisable = false;
        teamMessageReceiverType = TeamMessageReceiverType.TERMINAL;
      }
      else if (subRegion_Id !== null) {
        this.countrySelectVisiable = false;
        teamMessageReceiverType = TeamMessageReceiverType.SUB_REGION;
      }
      else {
        teamMessageReceiverType = TeamMessageReceiverType.COUNTRY;
      }

      this.store.dispatch(new UpdateFormValue({
        value: { teamMessageReceiverType: teamMessageReceiverType, country: country, subRegion_Id: subRegion_Id, terminal_Ids: terminals },
        path: this.formPath
      }));

      this.countrySelectItems = SiteConfigHelper.GetCountryCodeDropDownList().filter(item => item.value === country);
    });
  }

  onReceiverTypeChange = (event: any) => {
    this.requiredSetUp(event.value);
  }

  requiredSetUp = (type: TeamMessageReceiverType) => {
    var countryField = this.editForm.get(TeamMessageFieldName.country);
    var subRegionField = this.editForm.get(TeamMessageFieldName.subRegion_Id);
    var terminalField = this.editForm.get(TeamMessageFieldName.terminal_Ids);
    var driverTerminalsField = this.editForm.get(TeamMessageFieldName.driverTerminals);
    var dirverField = this.editForm.get(TeamMessageFieldName.driver_Ids);

    countryField.clearValidators();
    subRegionField?.clearValidators();
    terminalField.clearValidators();
    driverTerminalsField.clearValidators();
    dirverField.clearValidators();

    switch (type) {
      case TeamMessageReceiverType.COUNTRY:
        countryField.addValidators(Validators.required);
        break;

      case TeamMessageReceiverType.SUB_REGION:
        subRegionField.addValidators(Validators.required);
        break;

      case TeamMessageReceiverType.TERMINAL:
        terminalField.addValidators(Validators.required);
        break;

      case TeamMessageReceiverType.DRIVER:
        driverTerminalsField.addValidators(Validators.required);
        dirverField.addValidators(Validators.required);
        break;
    }

    countryField.updateValueAndValidity();
    subRegionField.updateValueAndValidity();
    terminalField.updateValueAndValidity();
    driverTerminalsField.updateValueAndValidity();
    dirverField.updateValueAndValidity();
  }

  onOkClick = () => {
    this.store.dispatch(new AppDataAction.UpdateTeamMessage());
  }

  driverTerminalsSelectionChanged = (event: any) => {
    this.store.dispatch([new AppDataAction.GetDriverByTerminal(event.value),
    new UpdateFormValue({
      value: { driver_Ids: [] },
      path: this.formPath
    })]);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.store.dispatch(new TerminateTask(NgxsActionName.GET_DRIVER_BY_TERMINAL));
  }

  private getTerminalIds = (profileMe: UserProfile) => {
    return (profileMe && profileMe.terminal_ids) ? profileMe.terminal_ids : [];
  }

  private getSubRegionIdIfNoTerminal = (profileMe: UserProfile) => {
    var terminals = this.getTerminalIds(profileMe);
    return terminals.length === 0 && profileMe.subRegion_ids && profileMe.subRegion_ids.length > 0 ? profileMe.subRegion_ids[0] : null;
  }
}
